import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
// import { DatePicker } from 'antd';
// import 'antd/dist/reset.css';
import './RecordPopup.css';
import { collection, getDocs, setDoc, doc, deleteDoc, getDoc, onSnapshot } from 'firebase/firestore';
import dayjs from 'dayjs';
import Papa from "papaparse";
import { FaEdit, FaTrash, FaTimes, FaCheck, FaPlus, FaInfoCircle, FaChartBar, FaDownload } from 'react-icons/fa';
import RecordStatusWatcher from './RecordStatusWatcher';
import { useNavigate } from 'react-router-dom';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SurveySelectionPopup from './SurveySelectionPopup';
import StatisticsModal from './StatisticsModal';


const RecordPopup = ({ userId, onClose }) => {
  const [showMemoryOnceCalendar, setShowMemoryOnceCalendar] = useState(false);
  const [showMemoryConsistentCalendar, setShowMemoryConsistentCalendar] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedMemoryValue, setSelectedMemoryValue] = useState(null);
  const [records, setRecords] = useState([]);
  const [isConsistent, setIsConsistent] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // State to track deletion progress
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null); // null if creating
  const [lastSelectedMemoryIndex, setLastSelectedMemoryIndex] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const fileInputRef = useRef(null);
  const [conflicts, setConflicts] = React.useState([]);
  const [validationMessage, setValidationMessage] = useState('');
  const selectedDate = startTime ? dayjs(startTime, 'DD-MM-YYYY HH:mm').toDate() : null;
  const now = new Date();
  const [touchedEndTime, setTouchedEndTime] = useState(false);
  const navigate = useNavigate();
  const [showSurveyPopup, setShowSurveyPopup] = useState(false);
  const [selectedSurveyAnswers, setSelectedSurveyAnswers] = useState([]);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [recordStatuses, setRecordStatuses] = useState({});
  const [showStatistics, setShowStatistics] = useState(false);
  const [isExportingHistory, setIsExportingHistory] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    // To store unsubscribe functions so we can clean up
    const unsubscribes = [];

    records.forEach((record) => {
      const docRef = doc(db, "EMA", userId, "Records", record.id);
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setRecordStatuses((prev) => ({
            ...prev,
            [record.id]: data.status || "Unknown",
          }));
        } else {
          setRecordStatuses((prev) => ({
            ...prev,
            [record.id]: "Not found",
          }));
        }
      });

      unsubscribes.push(unsubscribe);
    });

    // Cleanup listeners when component unmounts or records change
    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [records, userId]);

  useEffect(() => {
    const statusDocRef = doc(db, 'EMA', userId, 'registered', 'status');
  
    const unsubscribe = onSnapshot(statusDocRef, (statusDocSnap) => {
      if (statusDocSnap.exists()) {
        const data = statusDocSnap.data();
        setLastSelectedMemoryIndex(convertMemoryToWebDispaly(data.lastSelectedMemoryIndex));
        setUpdatedAt(data.updatedAt);
      } else {
        console.log('No such document!');
      }
    }, (error) => {
      console.error('Error listening to memory index updates:', error);
    });
  
    return () => unsubscribe(); // Clean up the listener on unmount
  }, [userId, db]);

  useEffect(() => {
    fetchRecords();
  }, [userId]);

  const fetchRecords = async () => {
    try {
      const recordsRef = collection(db, `EMA/${userId}/Records`);
      const snapshot = await getDocs(recordsRef);
      const recordsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => a.start - b.start);
      console.log('Sorted records:', recordsList);
      setRecords(recordsList);
    } catch (error) {
      console.error('Error fetching records:', error);
    }
  };

  // Exports this participant's entire history — scheduled records (trial plan),
  // device-reported memory events (field execution), and every completed
  // survey's full question/answer pairs — as a single long-format CSV.
  const exportParticipantHistory = async () => {
    setIsExportingHistory(true);
    try {
      const [recordsSnap, deviceMemorySnap, surveysSnap] = await Promise.all([
        getDocs(collection(db, `EMA/${userId}/Records`)),
        getDocs(collection(db, `EMA/${userId}/deviceMemory`)),
        getDocs(collection(db, `EMA/${userId}/surveys`)),
      ]);

      const toISO = (unixSeconds) =>
        typeof unixSeconds === 'number' ? new Date(unixSeconds * 1000).toISOString() : '';

      // Memories are stored 0-based; export them 1-based like the UI (memory 1 = 1).
      const toMemoryNumber = (memoryIndex) =>
        memoryIndex === '' || memoryIndex === null || memoryIndex === undefined || isNaN(Number(memoryIndex))
          ? ''
          : convertMemoryToWebDispaly(Number(memoryIndex));

      const rows = [];

      recordsSnap.forEach((docSnap) => {
        const data = docSnap.data();
        rows.push({
          recordType: 'Record',
          recordId: docSnap.id,
          type: data.type || '',
          status: data.status || '',
          isConsistent: data.isConsistent ?? '',
          start: data.start ?? '',
          startISO: toISO(data.start),
          end: data.end ?? '',
          endISO: toISO(data.end),
          value: data.type === 'Survey' ? (data.value ?? '') : toMemoryNumber(data.value),
          title: data.title || '',
          conditions: data.conditions || '',
          surveyId: '',
          question: '',
          answer: '',
        });
      });

      deviceMemorySnap.forEach((docSnap) => {
        const data = docSnap.data();
        rows.push({
          recordType: 'DeviceMemoryEvent',
          recordId: docSnap.id,
          type: '',
          status: '',
          isConsistent: '',
          start: data.updatedAt ?? '',
          startISO: toISO(data.updatedAt),
          end: '',
          endISO: '',
          value: toMemoryNumber(data.lastSelectedMemoryIndex),
          title: '',
          conditions: '',
          surveyId: '',
          question: '',
          answer: '',
        });
      });

      surveysSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const answers = Array.isArray(data.answers) && data.answers.length > 0
          ? data.answers
          : [{ question: '', answer: '' }];

        answers.forEach((a) => {
          rows.push({
            recordType: 'SurveyAnswer',
            recordId: docSnap.id,
            type: 'Survey',
            status: '',
            isConsistent: '',
            start: data.timestamp ?? '',
            startISO: toISO(data.timestamp),
            end: '',
            endISO: '',
            value: '',
            title: data.title || '',
            conditions: '',
            surveyId: data.surveyId || '',
            question: a.question || '',
            answer: a.answer || '',
          });
        });
      });

      rows.sort((a, b) => (a.start || 0) - (b.start || 0));

      const csv = Papa.unparse({
        fields: [
          'recordType', 'recordId', 'type', 'status', 'isConsistent',
          'start', 'startISO', 'end', 'endISO', 'value', 'title', 'conditions',
          'surveyId', 'question', 'answer',
        ],
        data: rows,
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${userId}_history_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting participant history:', error);
      alert('Failed to export participant history. Please try again.');
    } finally {
      setIsExportingHistory(false);
    }
  };

  const handleMemorySubmit = async () => {
    if (!startTime || !selectedMemoryValue || (isConsistent && !endTime)) return;
    console.log("startTime: ", startTime);
    console.log("endTime: ", endTime);
    if (startTime > endTime) {
      alert("Start time need to be earlier than end time.");
      return;
    }
      try {
      const currentTimestamp = Math.floor(Date.now() / 1000);
      const startTimestamp = Math.floor(new Date(startTime).getTime() / 1000);
      const endTimestamp = isConsistent ? Math.floor(new Date(endTime).getTime() / 1000) : null;
  
      // Check for conflicts before saving
      const conflict = records.some((record) => {
        if (editingRecord && record.id === editingRecord.id) return false;
        const recordStart = record.start;
        const recordEnd = record.end || null;
        const recordIsConsistent = record.isConsistent;
  
        console.log("startTimestamp: ", startTimestamp);
        console.log("startTime: ", startTime);
        console.log("recordStart: ", recordStart);
        console.log("recordEnd: ", recordEnd);
        console.log("#####");

      if (!recordIsConsistent) {
          console.log("conflict not consistent startTimestamp == recordStart = ", startTimestamp == recordStart);
          return startTimestamp == recordStart
        } else if (recordStart && recordEnd) {
          console.log("conflict consistent recordStart <= startTimestamp && startTimestamp <= recordEnd = ", (recordStart <= startTimestamp && startTimestamp <= recordEnd));
          return (recordStart <= startTimestamp && startTimestamp < recordEnd)
        } else if (recordIsConsistent && recordStart && recordEnd) {
          console.log("conflict consistent startTimestamp >= recordStart && startTimestamp < recordEnd = ", (startTimestamp >= recordStart && startTimestamp < recordEnd));
          console.log("conflict consistent endTimestamp > recordStart && endTimestamp <= recordEnd = ", (endTimestamp > recordStart && endTimestamp <= recordEnd));
          console.log("conflict consistent startTimestamp <= recordStart && endTimestamp >= recordEnd = ", (startTimestamp <= recordStart && endTimestamp >= recordEnd)); 
          return (
            (startTimestamp >= recordStart && startTimestamp < recordEnd) ||
            (endTimestamp > recordStart && endTimestamp <= recordEnd) ||
            (startTimestamp <= recordStart && endTimestamp >= recordEnd)
          );
        }
  
        return false;
      });
  
      if (conflict) {
        alert("Time conflict with existing record.");
        return;
      }
  
      const recordId = editingRecord ? editingRecord.id : currentTimestamp.toString();
      const status = editingRecord ? editingRecord.status : "Pending";
      const recordsRef = doc(db, `EMA/${userId}/Records`, recordId);
      const data = {
        start: startTimestamp,
        value: selectedMemoryValue - 1,
        createdAt: currentTimestamp,
        isConsistent: isConsistent,
        status: status
      };
  
      if (isConsistent) {
        data.end = endTimestamp;
        data.type = "MemoryConsistent";
      } else {
        data.type = "MemoryOnce";
      }
  
      await setDoc(recordsRef, data);
  
      fetchRecords();
      setStartTime('');
      setEndTime('');
      setEditingRecord(null);
      setSelectedMemoryValue(null);
      setShowMemoryOnceCalendar(false);
      setShowMemoryConsistentCalendar(false);
    } catch (error) {
      console.error('Error submitting memory record:', error);
    }
  };

  const handleDelete = async (recordId) => {
    const confirmed = window.confirm("Are you sure you want to delete this record?");
    if (!confirmed) return; // user clicked Cancel
  
    try {
      setIsDeleting(true);
      const recordRef = doc(db, `EMA/${userId}/Records`, recordId);
      await deleteDoc(recordRef);
      fetchRecords();
    } catch (error) {
      console.error('Error deleting record:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAllRecords = async () => {
    if (records.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete all ${records.length} event(s) for ${userId}? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setIsDeletingAll(true);
      await Promise.all(
        records.map((record) => deleteDoc(doc(db, `EMA/${userId}/Records`, record.id)))
      );
      fetchRecords();
    } catch (error) {
      console.error('Error deleting all records:', error);
      alert('Failed to delete all events. Please try again.');
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleShowInfo = async (record) => {
    try {
      const docRef = doc(db, `/EMA/${userId}/surveys/${record.id}`);
    //  const docRef = doc(db, `/EMA/c4423f1a-e844-4b6c-a737-657bdda2990f/surveys/896MU5Dms7rcy0H8BcZo`);

      
      const docSnap = await getDoc(docRef);
  
      if (docSnap.exists()) {
        const data = docSnap.data();
        setSelectedSurveyAnswers(data.answers || []);
        setShowSurveyModal(true);
      } else {
        console.error("No such document: ", record.id);
      }
    } catch (error) {
      console.error("Error fetching survey:", error);
    }
  };

  const handleEdit = (record) => {

   setStartTime(record.start * 1000);
    console.log('record: ', record);
    if (record.end) {
      setEndTime(record.end * 1000);
    }
    setSelectedMemoryValue(record.value + 1);
    setShowMemoryOnceCalendar(record.type === "MemoryOnce");
    setShowMemoryConsistentCalendar(record.type === "MemoryConsistent");
    setIsConsistent(record.isConsistent);

    if (record.type === "Survey") {
      setEditingSurvey(record); // <-- set the survey to edit
      setShowSurveyPopup(true); // <-- open the popup
      return;
    }

    setEditingRecord(record);
  };

  function handleValidate() {
    if (!records || records.length === 0) {
      console.log("No records to validate");
      return [];
    }
    const sortedRecords = [...records].sort((a, b) => a.start - b.start).filter(r => r.type !== "Survey");
    const conflictIds = new Set();
  
    // Check duplicate starts
    for (let i = 1; i < sortedRecords.length; i++) {
      console.log("sortedRecords[i]: ", sortedRecords[i]);
      if (sortedRecords[i].start === sortedRecords[i - 1].start) {
        conflictIds.add(sortedRecords[i].id);
        conflictIds.add(sortedRecords[i - 1].id);
      }
    }
  
    // Check overlaps
    for (let i = 1; i < sortedRecords.length; i++) {
      const prev = sortedRecords[i - 1];
      const curr = sortedRecords[i];
  
      if (curr.start < prev.end) {
        conflictIds.add(curr.id);
        conflictIds.add(prev.id);
      }
    }
  
    if (conflictIds.size > 0) {
      console.warn("Conflicts found in records:", Array.from(conflictIds));
    } else {
      console.log("No conflicts detected.");
    }
  
    return Array.from(conflictIds);
  }

  function onValidateClick() {
    const conflictIds = handleValidate();
    setConflicts(conflictIds);
  
    if (conflictIds.length > 0) {
      setValidationMessage(`⚠️ Found ${conflictIds.length} conflicting record(s).`);
    } else {
      setValidationMessage('✅ No conflicts found. All records are valid.');
    }
  
    // Hide message after 5 seconds
    setTimeout(() => {
      setValidationMessage('');
    }, 5000);
  }


  const formatDateInputValue = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
  
    const pad = (num) => String(num).padStart(2, '0');
  
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
  
    let hours = date.getHours();
    const minutes = pad(date.getMinutes());
    const ampm = hours >= 12 ? 'PM' : 'AM';
  
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12
    const formattedHours = pad(hours);
  
    return `${year}-${month}-${day} ${formattedHours}:${minutes} ${ampm}`;
  };
  
  

  const handleButtonClick = () => {
    fileInputRef.current.click(); // trigger hidden file input
  };
  
  function parseDateTime(dateStr, timeStr) {
    const [day, month, year] = dateStr.split("/").map(Number);
    const [hours, minutes] = timeStr.split(":").map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }

  const getDateOnly = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    console.log("Selected file:", file.name);
  
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        console.log("CSV parsing complete:", results);
  
        if (results.errors.length) {
          console.error("CSV errors:", results.errors);
          alert("CSV parse error. See console.");
          return;
        }
  
        const csvData = results.data;
        console.log("Parsed rows:", csvData.length);
      
        const firstStartDate = parseDateTime(csvData[0].start_date, csvData[0].start_time);

        // Ask user for new base start date
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const defaultDate = `${pad(today.getDate())}/${pad(today.getMonth() + 1)}/${today.getFullYear()}`;
        const userDateInput = prompt("Pick start date (dd/mm/yyyy)", defaultDate);
        if (!userDateInput || !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(userDateInput)) {
          alert("Invalid date format. Use dd/mm/yyyy");
          e.target.value = null;
          return;
        }

        const [uDay, uMonth, uYear] = userDateInput.split('/').map(Number);
        const userBaseDate = new Date(uYear, uMonth - 1, uDay); // time is 00:00

        try {
          const uploadPromises = csvData.map(async (row, i) => {
            console.log(`Processing row ${i + 1}`, row);
  
             const currentTimestamp = Math.floor(Date.now() / 1000) + i;
             const recordRef = doc(db, `EMA/${userId}/Records`, currentTimestamp.toString());

            // //const startDate = parseDateTime(row.start_date, row.start_time);
            // const newStartDate = new Date(userBaseDate.getTime() + gaps[i]);
            // const startTimestamp = Math.floor(newStartDate.getTime() / 1000);

            const originalStart = parseDateTime(row.start_date, row.start_time);


            console.log(`Processing row firstStartDate ${i + 1}`, firstStartDate);
            console.log(`Processing row originalStart ${i + 1}`, originalStart);

            // Calculate day gap only
            const dayGap = Math.floor(
              (getDateOnly(originalStart) - getDateOnly(firstStartDate)) / (1000 * 60 * 60 * 24)
            );
            console.log(`Processing row dayGap ${i + 1}`, dayGap);

            // Set start time from original row
            const [hours, minutes] = row.start_time.split(':').map(Number);
            const shiftedStart = new Date(userBaseDate);
            shiftedStart.setDate(shiftedStart.getDate() + dayGap);
            shiftedStart.setHours(hours, minutes);
            const startTimestamp = Math.floor(shiftedStart.getTime() / 1000);

            // A "SURVEY" row's selected_memory column holds a surveyId, not a
            // memory index — schedule it as a Survey-type Record instead.
            if ((row.event_type || '').toUpperCase() === 'SURVEY') {
              const surveyId = row.selected_memory;
              let surveyTitle = '';
              try {
                const surveySnap = await getDoc(doc(db, 'EMA', 'data', 'surveys', surveyId));
                if (surveySnap.exists()) {
                  surveyTitle = surveySnap.data().title || '';
                }
              } catch (err) {
                console.error(`Failed to fetch survey title for ${surveyId}:`, err);
              }

              const surveyRecord = {
                type: "Survey",
                start: startTimestamp,
                value: surveyId,
                createdAt: currentTimestamp,
                isConsistent: false,
                status: "Pending",
                title: surveyTitle,
                conditions: row.conditions || '',
              };

              console.log("Uploading survey record:", surveyRecord);
              await setDoc(recordRef, surveyRecord);
              return;
            }

            let endTimestamp = null;
            const isConsistent = row.end_time_required.toLowerCase() === 'true';
            if (isConsistent && row.end_date && row.end_time) {
              const originalEnd = parseDateTime(row.end_date, row.end_time);
              const endDayGap = Math.floor(
                (getDateOnly(originalEnd) - getDateOnly(firstStartDate)) / (1000 * 60 * 60 * 24)
              );
              const [eh, em] = row.end_time.split(':').map(Number);
              const shiftedEnd = new Date(userBaseDate);
              shiftedEnd.setDate(shiftedEnd.getDate() + endDayGap);
              shiftedEnd.setHours(eh, em);
              endTimestamp = Math.floor(shiftedEnd.getTime() / 1000);
            }

            const record = {
              start: startTimestamp,
              value: convertMemoryToApp(Number(row.selected_memory)),
              isConsistent,
              createdAt: currentTimestamp,
              status: "Pending",
              conditions: row.conditions || '',
            };

            if (isConsistent) {
              record.end = endTimestamp;
              record.type = "MemoryConsistent";
            } else {
              record.type = "MemoryOnce";
            }

            console.log("Uploading memory record:", record);
            await setDoc(recordRef, record);
          });
  
          await Promise.all(uploadPromises);
          alert('CSV uploaded successfully!');
          fetchRecords();
          e.target.value = null;
  
        } catch (err) {
          console.error('Upload error:', err);
          alert('Failed to upload CSV.');
        }
      },
    });
  };
  

  function convertMemoryToWebDispaly(memoryValue) {
    memoryValue += 1;
    return memoryValue;
  }

  function convertMemoryToApp(memoryValue) {
    memoryValue -= 1;
    return memoryValue;
  }

  function isSameDay(date1, date2) {
    return (
      date1 &&
      date2 &&
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  const allStatuses = Array.from(new Set(records.map(r => recordStatuses[r.id] || "Unknown")));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg max-w-7xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex flex-col gap-6">
              <span className="text-xs font-normal text-gray-500 italic">{userId}</span>
              <h2 className="text-2xl font-bold text-gray-900 text-left">Records</h2>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center bg-gray-50 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Current Memory Section */}
          {lastSelectedMemoryIndex !== null ? (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-4">
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Current Memory:</span> {lastSelectedMemoryIndex}
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Updated At:</span> {formatDateInputValue(updatedAt)}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-gray-500 mb-6">Loading memory index...</div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 mb-6">
            <button
              onClick={() => {
                setIsConsistent(true);
                setShowMemoryConsistentCalendar(true);
                setStartTime(null);
                setEndTime(null);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Record
            </button>
            <button
              onClick={handleButtonClick}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add CSV
            </button>
            <button
              onClick={() => setShowSurveyPopup(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Survey
            </button>
            <button
              onClick={() => setShowStatistics(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center"
            >
              <FaChartBar className="h-5 w-5 mr-2" />
              Statistics
            </button>
            <button
              onClick={exportParticipantHistory}
              disabled={isExportingHistory}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaDownload className="h-5 w-5 mr-2" />
              {isExportingHistory ? 'Exporting...' : 'Export History'}
            </button>
            <button
              onClick={handleDeleteAllRecords}
              disabled={isDeletingAll || records.length === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaTrash className="h-5 w-5 mr-2" />
              {isDeletingAll ? 'Deleting...' : 'Delete All Events'}
            </button>
          </div>

          {/* Records List */}
          <div className="bg-white rounded-lg border border-gray-200 mb-6">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
            
              <h3 className="text-lg font-medium text-gray-900">Existing Records</h3>

              <div className="flex items-left gap-2">
              {/* Status tags */}
                <span
                  onClick={() => setStatusFilter(null)}
                  className={`cursor-pointer px-3 py-1 rounded-full border ${statusFilter === null ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                >
                  All
                </span>
                {allStatuses.map(status => (
                  <span
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`cursor-pointer px-3 py-1 rounded-full border ${statusFilter === status ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    {status}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                
                {/* Validate button */}
                <button 
                  onClick={onValidateClick}
                  className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <FaCheck className="h-4 w-4" />
                </button>
              </div>
            </div>

            {validationMessage && (
              <div className={`p-4 ${conflicts.length > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {validationMessage}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Conditions</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records
                    .filter(record => statusFilter === null || (recordStatuses[record.id] || "Unknown") === statusFilter)
                    .map(record => (
                    
                    <tr 
                      key={record.id} 
                      className={`${conflicts.includes(record.id) ? 'bg-red-50' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.type == "Survey" ? record.title : convertMemoryToWebDispaly(record.value)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateInputValue(record.start)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateInputValue(record.end)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                      
                        {recordStatuses[record.id]}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs break-words">
                        {record.conditions || ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateInputValue(record.id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => handleEdit(record)} 
                            className="w-10 h-10 flex items-center justify-center bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                            title="Edit"
                          >
                            <FaEdit className="h-6 w-6" />
                          </button>
                          <button 
                            onClick={() => handleDelete(record.id)} 
                            className="w-10 h-10 flex items-center justify-center bg-red-50 text-red-600 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200"
                            title="Delete"
                          >
                            <FaTrash className="h-6 w-6" />
                          </button>
                          {record.type === "Survey" && recordStatuses[record.id] === "Complete" && (
                            <button 
                              onClick={() => handleShowInfo(record)} 
                              className="w-10 h-10 flex items-center justify-center bg-green-50 text-green-600 rounded-lg hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200"
                              title="More Info"
                            >
                              <FaInfoCircle className="h-6 w-6" />
                            </button>
                          )}

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {showSurveyModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                  <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl">
                    <h2 className="text-xl font-semibold mb-4">Survey Answers</h2>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedSurveyAnswers.map((item, index) => (
                        <div key={index} className="p-3 border rounded-lg">
                          <p className="font-medium text-gray-700">
                            {index + 1}. Q: {item.question}
                          </p>
                          <p className="text-gray-600">A: {item.answer}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 text-right">
                      <button
                        onClick={() => setShowSurveyModal(false)}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Calendar Popup */}
          {(showMemoryOnceCalendar || showMemoryConsistentCalendar) && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl w-full mx-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Select Date and Time</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type:</label>
                  <div className="flex space-x-4">
                    <button 
                      className={`px-4 py-2 rounded-lg ${
                        showMemoryOnceCalendar 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        setIsConsistent(false);
                        setShowMemoryOnceCalendar(true);
                        setShowMemoryConsistentCalendar(false);
                      }}
                    >
                      Once
                    </button>
                    <button 
                      className={`px-4 py-2 rounded-lg ${
                        showMemoryConsistentCalendar 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        setIsConsistent(true);
                        setShowMemoryOnceCalendar(false);
                        setShowMemoryConsistentCalendar(true);
                      }}
                    >
                      Consist
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      When should the memory task start?
                    </label>
                    <DatePicker
                      selected={startTime ? new Date(startTime) : null}
                      onChange={(date) => {
                        const time = date.getTime();
                        setStartTime(time);
                        setEndTime(time + 60 * 60 * 1000);
                      }}
                      showTimeSelect
                      timeFormat="hh:mm aa"
                      timeIntervals={15}
                      dateFormat="dd-MM-yyyy hh:mm aa"
                      timeCaption="Time"
                      placeholderText="Select date and time"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      minDate={now}
                      minTime={
                        !startTime || isSameDay(new Date(startTime), now)
                          ? now
                          : new Date(new Date().setHours(0, 0, 0, 0))
                      }
                      maxTime={new Date(new Date().setHours(23, 45, 0, 0))}
                    />
                  </div>

                  {showMemoryConsistentCalendar && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date and Time
                      </label>
                      <DatePicker
                        selected={endTime ? new Date(endTime) : null}
                        onChange={(date) => setEndTime(date.getTime())}
                        onFocus={() => {
                          if (!startTime) {
                            setTouchedEndTime(true);
                          }
                        }}
                        showTimeSelect
                        timeFormat="hh:mm aa"
                        timeIntervals={15}
                        dateFormat="dd-MM-yyyy hh:mm aa"
                        timeCaption="Time"
                        placeholderText="Select end date and time"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={!startTime}
                        minDate={startTime ? new Date(startTime) : now}
                        minTime={
                          isSameDay(new Date(startTime), new Date(endTime))
                            ? new Date(startTime)
                            : new Date(new Date().setHours(0, 0, 0, 0))
                        }
                        maxTime={new Date().setHours(23, 45, 0, 0)}
                      />
                      {!startTime && touchedEndTime && (
                        <p className="mt-2 text-sm text-red-600">
                          Please select a start time first.
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Memory Value:
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[1, 2, 3, 4, 5, 6].map((value) => (
                        <button
                          key={value}
                          className={`px-4 py-2 rounded-lg ${
                            selectedMemoryValue === value
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          onClick={() => setSelectedMemoryValue(value)}
                        >
                          Memory {value}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowMemoryOnceCalendar(false);
                        setShowMemoryConsistentCalendar(false);
                        setSelectedMemoryValue(null);
                        setStartTime(null);
                        setEndTime(null);
                        setEditingRecord(null);
                        setSelectedMemoryValue(null);
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      onClick={handleMemorySubmit}
                      disabled={!selectedMemoryValue}
                      className={`px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        !selectedMemoryValue
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {showSurveyPopup && (
        <SurveySelectionPopup
          onClose={() => {
            setShowSurveyPopup(false);
            setEditingSurvey(null);
          }}
          userId={userId}
          editingSurvey={editingSurvey} // <-- pass the survey to edit
          onSurveyAdded={fetchRecords}
        />
      )}

      <StatisticsModal userId={userId} open={showStatistics} onClose={() => setShowStatistics(false)} />
    </div>
  );
};

export default RecordPopup; 